import {
  Basket,
  BasketItem,
  BasketRow,
  BasketRowsGroup,
} from 'graphql-api/baskets-api-interfaces';

import {
  BasketLineItem,
  BasketLinesEntity,
  BasketLineType,
} from '../../models';
import {
  isAddressGroup,
  isBrandGroup,
  isOrderNumberGroup,
} from '../basket-group-types/basket-group-types';
import { extractBasketRows } from '../extract-basket-rows/extract-basket-rows';
import { hasBackOrderBasketItems } from '../is-back-order-basket-item/is-back-order-basket-item';

export function getBasketLinesEntity(basket: Basket): BasketLinesEntity {
  return getFlattenedBasketGroupLines(basket.groups);
}

export function getFlattenedBasketGroupLines(
  basketGroups: BasketRowsGroup[],
  parentId: string | null = null,
): BasketLinesEntity {
  return basketGroups.reduce((basketLinesEntity, group) => {
    const basketLineItem = mapGroupToLineItem(group, parentId);

    basketLinesEntity[basketLineItem.id] = basketLineItem;

    if (group.groups?.length) {
      return {
        ...basketLinesEntity,
        ...getFlattenedBasketGroupLines(group.groups, basketLineItem.id),
      };
    }

    if (group.rows) {
      return {
        ...basketLinesEntity,
        ...getFlattenedBasketRowLines(group.rows, basketLineItem.id),
      };
    }

    return basketLinesEntity;
  }, {} as BasketLinesEntity);
}

export function getFlattenedBasketRowLines(
  rows: BasketRow[],
  parentId: string,
): BasketLinesEntity {
  return rows.reduce((basketRowEntity, row) => {
    const lineItem = mapRowToLineItem(row, parentId);
    return {
      ...basketRowEntity,
      [lineItem.id]: lineItem,
    };
  }, {} as BasketLinesEntity);
}

export const mapRowToLineItem = (
  row: BasketRow,
  parentId: string,
): BasketLineItem => ({
  id: getBasketRowLineUniqueId(row),
  basketItemIds: row.basketItems.map(({ basketItemId }) => basketItemId),
  basketItems: row.basketItems,
  parentId,
  type: BasketLineType.ROW,
  data: row,
  productLines: [],
});

export function mapGroupToLineItem(
  group: BasketRowsGroup,
  parentId: string | null,
): BasketLineItem {
  const id = getBasketGroupLineUniqueId(group, parentId);
  const rows = extractBasketRows([group]);
  const basketItems = getProductLinesBasketItems(rows);

  return {
    id,
    basketItems,
    basketItemIds: basketItems.map(({ basketItemId }) => basketItemId),
    parentId,
    type: BasketLineType.GROUP,
    data: group,
    productLines: rows,
    hasBackOrderBasketItems: hasBackOrderBasketItems(basketItems),
  };
}

export const getProductLinesBasketItems = (rows: BasketRow[]): BasketItem[] =>
  rows.reduce(
    (basketItems, row) => [...basketItems, ...row.basketItems],
    [] as BasketItem[],
  );

export const getBasketGroupLineUniqueId = (
  group: BasketRowsGroup,
  parentId?: string | null,
): string => `${parentId ?? ''}-${group.subTitle}-${group.title}-${group.id}`;

export const getBasketRowLineUniqueId = (row: BasketRow): string =>
  row.basketItems[0].basketItemId;

export const getBasketRowLines = (lines: BasketLineItem[]) =>
  lines.filter(({ type }) => type === BasketLineType.ROW);
export const getBasketGroupLines = (lines: BasketLineItem[]) =>
  lines.filter(({ type }) => type === BasketLineType.GROUP);

export const getBasketAddressGroupLines = (lines: BasketLineItem[]) =>
  lines.filter(({ data }) => isAddressGroup(data as BasketRowsGroup));

export const getBasketBrandGroupLines = (lines: BasketLineItem[]) =>
  lines.filter(({ data }) => isBrandGroup(data as BasketRowsGroup));

export const getBasketOrderNumberGroupLines = (lines: BasketLineItem[]) =>
  lines.filter(({ data }) => isOrderNumberGroup(data as BasketRowsGroup));
